import type { CSSProperties } from "react"

import type { Fila } from "./contenido"
import { ANCHO_MINUSCULA, ANCHO_VERSAL } from "./tipografia"

/** Cuerpos y espaciado de las filas, en mm. */
const ETIQUETA = 1.9
const ESPACIADO_ETIQUETA = 0.25
const VALOR = 2.3
const REALCE = 3.4
const MINIMO_CORREO = 1.8

/**
 * Filas «etiqueta : valor», con las etiquetas y los dos puntos en columna, como en el
 * diseño pedido.
 *
 * ⚠ **Una rejilla y no texto con espacios.** Con espacios los dos puntos sólo se
 * alinean en una letra monoespaciada; con la estrecha, «Cédula» e «Ingreso» miden
 * distinto y cada fila empezaría el valor en otro sitio. Los dos puntos van al final
 * de la etiqueta, que se estira a lo ancho de su columna: así caen todos en la misma
 * vertical y el `<dl>` sigue siendo HTML válido.
 *
 * ⚠ **Las columnas NO se estiran, y la de los valores puede encoger.** Estiradas, en
 * el reverso los dos puntos quedaban a cinco milímetros de «AVISAR A»; y sin poder
 * encoger, un correo largo empujaba la rejilla fuera de la tarjeta y cortaba las
 * etiquetas por la izquierda.
 */
export function Filas({
  filas,
  ancho,
  style,
}: {
  filas: Fila[]
  /** El ancho disponible, en mm: de él sale lo que le queda a cada valor. */
  ancho: number
  style?: CSSProperties
}) {
  if (filas.length === 0) return null
  // Lo que ocupa la columna de las etiquetas: la más larga, sus dos puntos y el hueco.
  const letras = Math.max(...filas.map((f) => f.etiqueta.length))
  const etiquetas = letras * ETIQUETA * ANCHO_VERSAL + (letras - 1) * ESPACIADO_ETIQUETA + 1.7
  const anchoValor = ancho - etiquetas - 1.2

  return (
    <dl
      style={{
        display: "grid",
        gridTemplateColumns: "max-content minmax(0, auto)",
        justifyContent: "start",
        maxWidth: "100%",
        columnGap: "1.2mm",
        rowGap: "0.35mm",
        alignItems: "baseline",
        margin: 0,
        textAlign: "left",
        ...style,
      }}
    >
      {filas.map((f) => (
        <div key={f.etiqueta} style={{ display: "contents" }}>
          <dt
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "1mm",
              fontSize: `${ETIQUETA}mm`,
              fontWeight: 600,
              letterSpacing: `${ESPACIADO_ETIQUETA}mm`,
              textTransform: "uppercase",
              color: "var(--impreso-gris)",
            }}
          >
            <span>{f.etiqueta}</span>
            <span aria-hidden={true}>:</span>
          </dt>
          <dd
            style={{
              margin: 0,
              fontSize: `${cuerpoDe(f, anchoValor)}mm`,
              fontWeight: f.realce ? 700 : 600,
              letterSpacing: f.realce ? "0.15mm" : undefined,
              lineHeight: 1.25,
              color: f.realce ? "var(--impreso-vino)" : "var(--impreso-tinta)",
            }}
          >
            {conCorte(f.valor)}
          </dd>
        </div>
      ))}
    </dl>
  )
}

/**
 * El cuerpo de un valor, en mm.
 *
 * ⚠ **Un correo encoge hasta que quepa su tramo más largo**, sin bajar de 1,8 mm. Es lo
 * único de la tarjeta que no se puede envolver por palabras —no tiene espacios— y los
 * corporativos de nombre compuesto pasan de los cuarenta caracteres.
 */
function cuerpoDe(fila: Fila, ancho: number): number {
  if (fila.realce) return REALCE
  if (!fila.valor.includes("@")) return VALOR
  const tramo = Math.max(...tramosDeCorreo(fila.valor).map((t) => t.length))
  return Math.max(MINIMO_CORREO, Math.min(VALOR, ancho / (tramo * ANCHO_MINUSCULA)))
}

/**
 * Un correo se parte DESPUÉS de la arroba y en ningún otro sitio: es su frontera de
 * palabra. Partido por letras sería lo que prohíbe `.claude/rules/frontend.md`.
 */
function tramosDeCorreo(correo: string): [string, string] {
  const arroba = correo.indexOf("@")
  return [correo.slice(0, arroba + 1), correo.slice(arroba + 1)]
}

function conCorte(valor: string) {
  if (!valor.includes("@")) return valor
  const [usuario, dominio] = tramosDeCorreo(valor)
  return (
    <>
      {usuario}
      <wbr />
      {dominio}
    </>
  )
}
